// PDP "Theater" gallery: one plate spotlit at a time. Transparent product
// renders float as specimens (`cut`), lifestyle shots mount as ivory photo
// plates (`photo`). Film-strip thumbs + arrows drive the stage; the counter
// and caption follow. "Tap to magnify" opens a native <dialog> lightbox with
// prev/next. Modes/captions come from the block markup (metafield-driven).
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

class LettoriaTheater extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[ref-stage]');
    this.cut = this.querySelector('[ref-cut]');
    this.photo = this.querySelector('[ref-photo]');
    this.caption = this.querySelector('[ref-caption]');
    this.plateNo = this.querySelector('[ref-plate-no]');
    this.stripNo = this.querySelector('[ref-strip-no]');
    this.lightbox = this.querySelector('dialog');
    this.lightboxImg = this.lightbox?.querySelector('img');
    this.thumbs = Array.from(this.querySelectorAll('.strip__t'));
    this.index = 0;
    this.swapGen = 0;
    this.ghost = null;
    this.preloadPlates();

    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.show(i)));
    this.querySelector('[ref-prev]')?.addEventListener('click', () => this.show(this.index - 1));
    this.querySelector('[ref-next]')?.addEventListener('click', () => this.show(this.index + 1));
    this.querySelector('[ref-zoom]')?.addEventListener('click', () => this.openLightbox());
    this.stage?.addEventListener('click', (e) => {
      if (e.target === this.cut || e.target === this.photo) this.openLightbox();
    });
    this.lightbox?.addEventListener('click', (e) => {
      if (e.target === this.lightbox) this.lightbox.close();
    });
    this.lightbox?.querySelector('[ref-lb-prev]')?.addEventListener('click', () => this.show(this.index - 1, true));
    this.lightbox?.querySelector('[ref-lb-next]')?.addEventListener('click', () => this.show(this.index + 1, true));
    this.lightbox?.querySelector('[ref-lb-close]')?.addEventListener('click', () => this.lightbox.close());
  }

  // Warm the browser cache with every plate once the page is idle, so
  // switching plates never waits on the network.
  preloadPlates() {
    const warm = () => this.thumbs.forEach((t) => {
      const img = new Image();
      img.src = t.getAttribute('data-src');
    });
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 4000 });
    else setTimeout(warm, 1500);
  }

  show(i, updateLightbox = false) {
    if (!this.thumbs.length) return;
    this.index = (i + this.thumbs.length) % this.thumbs.length;
    const thumb = this.thumbs[this.index];
    this.thumbs.forEach((t) => t.classList.remove('is-on'));
    thumb.classList.add('is-on');

    if (updateLightbox && this.lightboxImg) this.lightboxImg.src = thumb.getAttribute('data-zoom') ?? thumb.getAttribute('data-src');

    this.swapPlate(thumb, this.index);
  }

  // Crossfade to the new plate: decode it off-screen first, then leave the
  // outgoing plate on top as a fading ghost — the stage never flashes blank,
  // even across cut↔photo switches. A generation counter drops stale swaps
  // when thumbs are spam-clicked.
  swapPlate(thumb, index) {
    const mode = thumb.getAttribute('data-mode') === 'cut' ? 'cut' : 'photo';
    const src = thumb.getAttribute('data-src');
    const incoming = mode === 'cut' ? this.cut : this.photo;
    const outgoing = this.stage.classList.contains('show-photo') ? this.photo : this.cut;
    if (incoming === outgoing && incoming.getAttribute('src') === src) return;
    const gen = ++this.swapGen;

    const loader = new Image();
    loader.src = src;
    const apply = () => {
      if (gen !== this.swapGen || !this.isConnected) return;
      // caption and counter move together with the image, so the figure line
      // never announces a plate the stage isn't showing yet
      if (this.caption) this.caption.textContent = thumb.getAttribute('data-cap') ?? '';
      if (this.plateNo) this.plateNo.textContent = ROMAN[index] ?? index + 1;
      if (this.stripNo) this.stripNo.textContent = index + 1;
      this.ghost?.remove();
      const ghost = outgoing.cloneNode();
      ghost.classList.add('stage__ghost');
      ghost.classList.remove('is-in');
      ghost.removeAttribute('ref-cut');
      ghost.removeAttribute('ref-photo');
      this.ghost = ghost;
      this.stage.appendChild(ghost);

      incoming.src = src;
      this.stage.classList.toggle('show-photo', mode === 'photo');
      incoming.classList.remove('is-in');
      void incoming.offsetWidth; /* restart the settle-in animation */
      incoming.classList.add('is-in');

      requestAnimationFrame(() => ghost.classList.add('is-out'));
      setTimeout(() => {
        ghost.remove();
        if (this.ghost === ghost) this.ghost = null;
      }, 420);
    };
    (loader.decode ? loader.decode().catch(() => {}) : Promise.resolve()).then(apply);
  }

  openLightbox() {
    if (!this.lightbox) return;
    const thumb = this.thumbs[this.index];
    if (this.lightboxImg && thumb) this.lightboxImg.src = thumb.getAttribute('data-zoom') ?? thumb.getAttribute('data-src');
    this.lightbox.showModal();
  }
}

if (!customElements.get('lettoria-theater')) {
  customElements.define('lettoria-theater', LettoriaTheater);
}
